import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunListResponse } from 'src/entities/ShipmentOrderLegRun.entity';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
  DeliveriesResponse,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';

// resetting 成功後、react-query キャッシュを直接書き換えて即座に UI 反映する（再取得なし）
// - deliveries キャッシュ: リセット対象 legRun と、それを参照する operations を各 delivery から除去
// - legRuns キャッシュ: 対象 legRun の `assigned=false, delivery_id=null` に書き換え（→ 未割当リストに現れる）
export const useApplyResetToCaches = (): ((resetLegRunIds: number[]) => void) => {
  const queryClient = useQueryClient();

  return useCallback((resetLegRunIds: number[]) => {
    const idSet = new Set(resetLegRunIds);

    queryClient.setQueriesData<InfiniteData<DeliveriesResponse>>(
      { queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((d) => ({
              ...d,
              shipmentOrderLegRuns: d.shipmentOrderLegRuns.filter(
                (lr) => lr.id == null || !idSet.has(lr.id),
              ),
              operations: d.operations.filter((op) => !idSet.has(op.shipmentOrderLegRunId)),
            })),
          })),
        };
      },
    );

    queryClient.setQueriesData<PlanningsDeliveryWithLegRunEntity[]>(
      { queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] },
      (old) => {
        if (!old) return old;
        return old.map((d) => ({
          ...d,
          shipmentOrderLegRuns: d.shipmentOrderLegRuns.filter(
            (lr) => lr.id == null || !idSet.has(lr.id),
          ),
          operations: d.operations.filter((op) => !idSet.has(op.shipmentOrderLegRunId)),
        }));
      },
    );

    // SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY は「単体エンティティ cache」と「無限スクロール cache」の
    // 両方で使われているため、prefix match で単体 cache にもヒットする。
    // infinite query（pages を持つ）だけを対象に更新する。
    queryClient.setQueriesData<InfiniteData<ShipmentOrderLegRunListResponse>>(
      { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
      (old) => {
        if (!old) return old;
        const maybePages = (old as { pages?: unknown }).pages;
        if (!Array.isArray(maybePages)) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((lr) => (
              lr.id != null && idSet.has(lr.id)
                ? { ...lr, assigned: false, delivery_id: null as unknown as string }
                : lr
            )),
          })),
        };
      },
    );
    // 上の setQueriesData は「既に loaded されているページの中の対象 legRun」しか更新できない。
    // OFF した車両の legRun が未ロードページにある場合、未割当リストに出てこなくなるので
    // refetch を強制発火する (loaded ページが server 最新値で再取得され、未割当として現れる)。
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });

    // resetting もサーバ側で version が進むので refetch させる
    void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
  }, [queryClient]);
};
